import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Menu, Phone, X } from "lucide-react";
import { moreLinks, primaryLinks, serviceLinks, linkId } from "./navigationData";
import { motion, useScroll } from "motion/react";
import { useMotionPreference } from "../hooks/useMotionPreference";

const Header = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const reduced = useMotionPreference();

  useEffect(() => { setOpenMenu(null); setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const dismiss = (event) => {
      if (!headerRef.current?.contains(event.target)) setOpenMenu(null);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        if (mobileOpen) menuButtonRef.current?.focus();
        else if (openMenu) headerRef.current?.querySelector(`[data-testid="nav-${openMenu}-toggle"]`)?.focus();
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen, openMenu]);

  const navLink = (item, prefix = "nav") => (
    <NavLink key={item.path} to={item.path} end={item.path === "/"}
      data-testid={`${prefix}-${linkId(item.path)}`} className="gh-nav-link">
      {item.title}
    </NavLink>
  );

  return (
    <header ref={headerRef} className="gh-header" data-testid="site-header">
      {!reduced && <motion.div className="gh-reading-progress" style={{ scaleX: scrollYProgress }} aria-hidden="true" data-testid="reading-progress" />}
      <div className="gh-header-inner">
        <Link to="/" className="gh-brand" data-testid="header-home-link" aria-label="Groundhogs Septics home">
          <img src="/seo-images/groundhogs-logo.png" alt="Groundhogs™ Environmental - Septic Solutions" width="76" height="64" />
          <span className="gh-brand-name" data-testid="header-brand-name">Groundhogs™<small>Septic systems</small></span>
        </Link>
        <nav aria-label="Main navigation" className="gh-desktop-nav" data-testid="desktop-navigation">
          {navLink(primaryLinks[0])}
          <div className="gh-nav-group" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpenMenu(null); }}>
            <button type="button" className={`gh-nav-link ${location.pathname.startsWith('/services') ? 'active' : ''}`}
              data-testid="nav-services-toggle" aria-expanded={openMenu === "services"} aria-controls="services-menu"
              onClick={() => setOpenMenu(openMenu === "services" ? null : "services")}>
              Services <ChevronDown size={14} className={openMenu === "services" ? "gh-rotate" : ""} />
            </button>
            {openMenu === "services" && <div id="services-menu" className="gh-dropdown" data-testid="nav-services-menu">
              {serviceLinks.map((service) => <Link key={service.slug} to={`/services/${service.slug}`} data-testid={`nav-service-${service.slug}`}>
                {service.title}<ArrowUpRight size={16} />
              </Link>)}
              <Link to="/services" className="gh-dropdown-all" data-testid="nav-all-services">View All Services <ArrowUpRight size={16} /></Link>
            </div>}
          </div>
          {primaryLinks.slice(1).map((item) => navLink(item))}
          <div className="gh-nav-group" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpenMenu(null); }}>
            <button type="button" className="gh-nav-link" data-testid="nav-more-toggle" aria-expanded={openMenu === "more"}
              aria-controls="more-menu" onClick={() => setOpenMenu(openMenu === "more" ? null : "more")}>
              More <ChevronDown size={14} className={openMenu === "more" ? "gh-rotate" : ""} />
            </button>
            {openMenu === "more" && <div id="more-menu" className="gh-dropdown gh-dropdown-small" data-testid="nav-more-menu">
              {moreLinks.map((item) => navLink(item, "nav-more"))}
            </div>}
          </div>
          {navLink({ title: "Contact", path: "/contact" })}
        </nav>
        <div className="gh-header-actions">
          <a href="tel:1-888-907-3777" className="gh-header-phone" data-testid="header-phone"><Phone size={16} />1-888-907-3777</a>
          <Link to="/contact" className="gh-button gh-header-estimate" data-testid="header-estimate">Get Free Estimate <ArrowUpRight size={16} /></Link>
          <button ref={menuButtonRef} type="button" className="gh-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"} aria-expanded={mobileOpen}
            aria-controls="mobile-navigation" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={23} /> : <Menu size={23} />}
          </button>
        </div>
      </div>
      {mobileOpen && <nav id="mobile-navigation" className="gh-mobile-nav" aria-label="Mobile navigation" data-testid="mobile-navigation">
        {navLink(primaryLinks[0], "mobile-nav")}
        <div className="gh-mobile-group">
          <p data-testid="mobile-services-label">Services</p>
          {serviceLinks.map((service) => <Link key={service.slug} to={`/services/${service.slug}`} data-testid={`mobile-service-${service.slug}`}>{service.title}<ArrowUpRight size={15} /></Link>)}
          <Link to="/services" data-testid="mobile-all-services">View All Services <ArrowUpRight size={15} /></Link>
        </div>
        {primaryLinks.slice(1).map((item) => navLink(item, "mobile-nav"))}
        <div className="gh-mobile-group"><p data-testid="mobile-more-label">More</p>{moreLinks.map((item) => navLink(item, "mobile-nav"))}</div>
        {navLink({ title: "Contact", path: "/contact" }, "mobile-nav")}
        <a href="tel:1-888-907-3777" className="gh-mobile-phone" data-testid="mobile-header-phone"><Phone size={18} />1-888-907-3777</a>
        <Link to="/contact" className="gh-button" data-testid="mobile-header-estimate">Get Free Estimate <ArrowUpRight size={17} /></Link>
      </nav>}
    </header>
  );
};

export default Header;